import { notify,
	AcceptedUploadFileTypes,
	type Message,
	type ProcessFileType,
	type SDK_GetFilesQuery,
	type UploadFileMessage,
	strings,
} from "@quickpoint.me/shared";
import { nanoid } from "nanoid";
import { useEffect } from "react";
import {
	useRecoilTransaction_UNSTABLE,
	useRecoilValue,
	useSetRecoilState,
} from "recoil";
import { Files, FilesKeyForUiRefresh } from "../state/fileState";
import {
	HostId,
	ProcessFileFamily,
	StackId,
	UploadFileIds,
} from "../state/uploaderState";

function createFileObjectFromFile(
	ownerName: string,
	stackId: string,
	file: File,
): ProcessFileType | false {
	const acceptableFile = AcceptedUploadFileTypes.some(
		(type) => type === file.type,
	);
	console.log(file.type);
	if (!acceptableFile) {
		notify(
			`${file.name} cannot be uploaded:  We can only accept, jpg, gif, png or webP files`,
		);
		return false;
	}
	const type = file.type as (typeof AcceptedUploadFileTypes)[number];

	return {
		altText: "",
		data: file,
		fileName: file.name,
		fileSize: file.size,
		fileType: type,
		id: nanoid(26),
		inUploadQ: false,
		ownerName,
		tags: [],
		uploadComplete: false,
		uploading: false,
		url: "",
		userDefinedName: "",
		stackId,
		uploadedOn: new Date().getTime(),
		cloudFlareName: "processing",
	};
}

function MessageHandler() {
	const setFiles = useSetRecoilState(Files);
	const setFilesKeyForUiRefresh = useSetRecoilState(FilesKeyForUiRefresh);
	const hostId = useRecoilValue(HostId);
	const setStackId = useSetRecoilState(StackId);
	const stackId = useRecoilValue(StackId);
	const processFiles = useRecoilTransaction_UNSTABLE(
		({ set }) =>
			(ownerName: string, stackId: string, files: File[]) => {
				const newIds: string[] = [];
				for (const file of files) {
					const newFile = createFileObjectFromFile(ownerName, stackId, file);
					if (newFile) {
						newIds.push(newFile.id);
						set(ProcessFileFamily(newFile.id), newFile);
					}
				}

				set(UploadFileIds, (ids) => [...ids, ...newIds]);
			},
	);

	useEffect(() => {
		const toUploaderBC = new BroadcastChannel(
			strings.broadcastChannel.toUploader,
		);
		const toAuthorBC = new BroadcastChannel(strings.broadcastChannel.author);

		const toAuthorMessage: Message = {
			hostId,
			type: "fileUploader",
			action: strings.messages.requestAllFiles,
		};
		toAuthorBC.postMessage(toAuthorMessage);

		toUploaderBC.onmessage = (e: MessageEvent) => {
			const data: UploadFileMessage = e.data;

			if (data?.action && data.action === strings.messages.fileUpload) {
				const files = data.payload as FileList;
				if (data?.ownerId && data.ownerName) {
					processFiles(data.ownerName, stackId, [...files]);
				} else {
					throw new Error("firebase uid and ownerName are required");
				}
			}

			if (data?.action && data.action === strings.messages.allFiles) {
				const files = data.payload as SDK_GetFilesQuery["getFiles"];
				setFiles(files);
				setFilesKeyForUiRefresh((key) => key + 1);
			}

			if (data?.action && data.action === strings.messages.codeAndFilter) {
				const { stackId } = data.payload as { stackId: string };
				setStackId(stackId);
			}
		};

		return () => {
			toUploaderBC.close();
			toAuthorBC.close();
		};
	}, [processFiles, setFiles, setFilesKeyForUiRefresh, hostId]);

	return <></>;
}

export default MessageHandler;
